import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaBook, FaPlus, FaSearch, FaArchive, FaUndo, FaTimes } from 'react-icons/fa';
import { apiFetch } from '../../utils/apiAdmin';
import { adminSessionUsable } from '../../utils/authAdmin';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';
import TmeWikiEditorPane from './components/TmeWikiEditorPane';
import { setScriptDirectory, setWikiNavigator } from '../../components/tmeWiki/tmeWikiNodeSupport';

/**
 * TME Wiki - the shell. Migration Experts' OWN procedures, admin-only.
 *
 * Planning/TME-WIKI-2026-09-02.md §10.1, §10.2, §10.3.
 *
 * ⚠️ NEVER CLIENT-VISIBLE. Nothing under pages/Client imports this or anything
 * in components/tmeWiki, and tests/tme-wiki-client-isolation.test.js reads the
 * imports rather than trusting the intention.
 */

export const STORAGE_KEY = 'tmeWikiState';

const API = `${process.env.REACT_APP_API_URL}/admin/tme-wiki`;

/**
 * How many forwarded messages the queue keeps. The pane consumes on every
 * render, so it can never be more than a tick behind; this only stops a tab
 * left open for a day growing the array without bound.
 */
const LIVE_QUEUE_MAX = 50;

/** The deep-link route a stored page anchor resolves to (§10.2). */
const PAGE_ROUTE = /^\/admin\/tme-wiki\/wikis\/([^/]+)\/pages\/([^/]+)$/;

const DEFAULT_STATE = {
    selectedWikiId: null,
    // §14A: the page you were READING. It was the one piece of state this page
    // did not keep - the tab came back with the right wiki selected and the
    // tree expanded where it was left, and an empty editor pane, every time.
    // Its own sibling has always persisted it (WikiTemplatesTab,
    // WikiProjectsTab); TME Wiki was built from those two files and dropped the
    // second field from the pair.
    selectedPageId: null,
    searchQuery: '',
    includeArchived: false,
    tagFilter: []
};

/**
 * Restored state is VALIDATED before use - the WikiEditor.js:16-23 pattern.
 *
 * A stored value that has since changed shape must fall back to the defaults,
 * not render a wiki id that is a number. Exported so the fallback is testable
 * without mounting the page.
 */
export function readStoredState(raw) {
    try {
        if (!raw) return { ...DEFAULT_STATE };
        const parsed = JSON.parse(raw);
        const valid = parsed
            && (parsed.selectedWikiId === null || typeof parsed.selectedWikiId === 'string')
            && (parsed.selectedPageId === null || parsed.selectedPageId === undefined
                || typeof parsed.selectedPageId === 'string')
            && typeof parsed.searchQuery === 'string'
            && typeof parsed.includeArchived === 'boolean'
            && Array.isArray(parsed.tagFilter)
            && parsed.tagFilter.every(tag => typeof tag === 'string');
        if (!valid) return { ...DEFAULT_STATE };
        return {
            selectedWikiId: parsed.selectedWikiId,
            // ⚠️ A page id is only meaningful WITH its wiki. Stored apart they
            // drift, and restoring one from another wiki 404s the pane on
            // arrival - so a missing wiki discards the page with it.
            selectedPageId: parsed.selectedWikiId ? (parsed.selectedPageId ?? null) : null,
            searchQuery: parsed.searchQuery,
            includeArchived: parsed.includeArchived,
            tagFilter: parsed.tagFilter
        };
    } catch (err) {
        console.error('Failed to read TME Wiki state:', err);
        return { ...DEFAULT_STATE };
    }
}

const TmeWiki = () => {
    // The deep link a stored page anchor resolves to (§10.2). A page in another
    // wiki is legitimate (D19), so the route names both halves.
    const { wikiId: routeWikiId, pageId: routePageId } = useParams();
    const navigate = useNavigate();

    const initial = readStoredState(localStorage.getItem(STORAGE_KEY));
    const [selectedWikiId, setSelectedWikiId] = useState(routeWikiId || initial.selectedWikiId);
    // §14A. The selection lives in the PANE (it is the pane that opens pages);
    // the persisted state is written HERE, so the value has to travel up - the
    // shape `onTagFilterChange` and `onUnsavedChanges` already use.
    const [selectedPageId, setSelectedPageId] = useState(
        routeWikiId ? routePageId || null : initial.selectedPageId
    );
    // ⚠️ Consumed ONCE, at mount. Left live it would re-open the restored page
    // every time the reader closed it, and re-open it again after a wiki
    // switch and back - the session's own selection has to win from then on.
    const restoredPageIdRef = useRef(
        !routeWikiId && initial.selectedWikiId ? initial.selectedPageId : null
    );
    const [searchQuery, setSearchQuery] = useState(initial.searchQuery);
    const [includeArchived, setIncludeArchived] = useState(initial.includeArchived);
    const [tagFilter, setTagFilter] = useState(initial.tagFilter);

    const [wikis, setWikis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newWikiName, setNewWikiName] = useState('');
    const [busy, setBusy] = useState(false);

    // Raised by the editor pane, and read by the beforeunload handler through a
    // ref so the listener is registered once rather than on every keystroke.
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const unsavedChangesRef = useRef(false);
    const scriptsLoadedRef = useRef(false);

    // Search (§8). Deliberately NOT persisted: a stale query restored on the
    // next visit would show results for something nobody asked for.
    const [pageQuery, setPageQuery] = useState('');
    const [searchAllWikis, setSearchAllWikis] = useState(false);
    const [results, setResults] = useState(null);
    const [searchError, setSearchError] = useState(null);
    const [searching, setSearching] = useState(false);
    const [requestedPageId, setRequestedPageId] = useState(null);
    // The same listing twice over, on purpose: the DIRECTORY is what the
    // inline node reads (it cannot take props), and the ARRAY is what the
    // link picker lists. One fetch feeds both.
    const [scripts, setScripts] = useState([]);

    useEffect(() => {
        unsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                selectedWikiId, selectedPageId, searchQuery, includeArchived, tagFilter
            }));
        } catch (err) {
            console.error('Failed to save TME Wiki state:', err);
        }
    }, [selectedWikiId, selectedPageId, searchQuery, includeArchived, tagFilter]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (unsavedChangesRef.current) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // ⚠️ SILENT MODE (LIV, D1). Called as it stands from a message path this
    // drops the WHOLE PAGE into its loading state, and blanks the wiki list on
    // any failure - for a request the reader never made.
    const fetchWikis = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const response = await apiFetch(`${API}/wikis?includeArchived=${includeArchived}`);
            if (!response.ok) throw new Error(`Failed to load wikis (${response.status})`);
            const data = await response.json();
            setWikis(data.wikis || []);
        } catch (err) {
            console.error('Failed to load TME wikis:', err);
            if (!silent) {
                setError(err.message);
                setWikis([]);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [includeArchived]);

    useEffect(() => {
        fetchWikis();
    }, [fetchWikis]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-14). THE WORK IS SPLIT THREE WAYS, AND THE SPLIT
    // FOLLOWS WHO FETCHES:
    //
    //   this page              the WIKI LIST only
    //   TmeWikiEditorPane      the page list, the open page, the compare and
    //                          the banner
    //   the two panels         their own contents, off a refresh key
    //
    // So the parent handles the message behind its gates, refreshes the wiki
    // list, and FORWARDS the message to the pane as a prop. An earlier revision
    // of the plan gave the parent the tree and both panels; it fetches none of
    // them, so that version would have refreshed nothing while appearing to.
    //
    // ⚠️ THE SEARCH RESULTS ARE DELIBERATELY NOT REFRESHED. A page renamed or
    // archived elsewhere leaves a stale hit until the search is re-run.
    // Refreshing them would silently re-run a query the operator typed and
    // change the result set under them - the same objection that makes the
    // Audit Trail use a counter rather than an insertion. A stale hit that
    // 404s on click is the better failure, and the page list beneath it is
    // live.
    // -----------------------------------------------------------------------
    // ⛔ A QUEUE, NOT A SLOT (Fable's build review, 2026-09-03). React batches
    // state updates, so several messages arriving in one tick would collapse to
    // the LAST one and the pane would never see the others - one for another
    // page and one for the open page, and the open page's refresh is gone.
    // Each entry carries a sequence; the pane consumes everything above the
    // last one it saw, so the queue can be trimmed without it losing its place.
    const [liveMessages, setLiveMessages] = useState([]);
    const liveSeqRef = useRef(0);
    const busyRef = useRef(busy);
    useEffect(() => { busyRef.current = busy; }, [busy]);
    const pendingWikiListRef = useRef(false);

    // GATE 0 lives HERE - at the point of refetch - not per message in the
    // socket handler (Fable, round 2): there it renewed the session for message
    // types this page ignores, and the deferred flush below reached apiFetch,
    // and its redirect, with no gate at all. This page holds an editor with
    // unsaved work in it, so that redirect is a data-loss path.
    const liveWikiListRefresh = useCallback(async () => {
        if (!await adminSessionUsable()) return;
        fetchWikis({ silent: true });
    }, [fetchWikis]);

    useAdminWebSocket(useCallback((message) => {
        // GATE 0 is inside liveWikiListRefresh (and the pane's own runner).
        // GATE 1 - type.
        if (message.type !== 'tme_wiki_updated') return;

        // ⛔ THE FORWARD IS NOT GATED. A dirty editor is exactly when the pane
        // needs the message - it is what raises D4's banner - so `busy` gates
        // the parent's OWN refetch and nothing else.
        liveSeqRef.current += 1;
        const entry = { seq: liveSeqRef.current, message };
        setLiveMessages(queue => [...queue, entry].slice(-LIVE_QUEUE_MAX));

        // GATE 2 - none for the wiki list: a wiki created elsewhere should
        // appear, so ANY tme_wiki_updated refreshes it.
        // GATE 3 - hold it while a wiki create/archive/restore is in flight.
        if (busyRef.current) {
            pendingWikiListRef.current = true;
            return;
        }
        liveWikiListRefresh();
    }, [liveWikiListRefresh]));

    // D11 - what gate 3 held runs once, when the action finishes.
    useEffect(() => {
        if (busy || !pendingWikiListRef.current) return;
        pendingWikiListRef.current = false;
        liveWikiListRefresh();
    }, [busy, liveWikiListRefresh]);

    /**
     * The Script Repository listing, fetched ONCE PER MOUNT and published for
     * the inline script node and (from TWK-21) the links panel.
     *
     * ⚠️ ONCE, NOT PER PAGE (H3, R25). `GET /admin/scripts` runs one COUNT query
     * PER SCRIPT to compute `linkCount` - scriptRead.js says so and explains why
     * it cannot be batched - so "the page already loads it" must not become
     * "every page navigation reloads the whole Script Repository count set".
     * `force` refreshes it when a link picker opens, so a script created since
     * this mount is still offered.
     */
    const loadScripts = useCallback(async (force = false) => {
        if (scriptsLoadedRef.current && !force) return;
        scriptsLoadedRef.current = true;
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts`);
            if (!response.ok) return;
            const data = await response.json();
            setScripts(data.scripts || []);
            setScriptDirectory(data.scripts || []);
        } catch (err) {
            console.error('Failed to load scripts for TME Wiki:', err);
        }
    }, []);

    useEffect(() => {
        loadScripts();
    }, [loadScripts]);

    // A page link is a route change rather than a reload, so the shell and its
    // neighbours stay mounted. Cleared on unmount: a navigator belonging to a
    // page that is gone would navigate nothing.
    //
    // ⚠️ THE SAME UNSAVED-CHANGES GUARD EVERY OTHER EXIT HAS (review finding 6).
    // An inline page link is the one way out of a dirty page that used to skip
    // the confirm; the node cannot see `dirty`, so the guard lives here.
    /** Open a wiki, and optionally a page in it. Every selection path ends here. */
    const chooseWiki = useCallback((wikiId, pageId = null) => {
        setSelectedWikiId(wikiId);
        setRequestedPageId(pageId);
        // §14A: the stored page belongs to the wiki it was read in. Switching
        // wiki must not leave the previous wiki's page id in the persisted
        // state, where the next visit would try to open it in the wrong wiki.
        setSelectedPageId(pageId || null);
        restoredPageIdRef.current = null;
    }, []);

    useEffect(() => {
        setWikiNavigator((path) => {
            if (unsavedChangesRef.current
                && !window.confirm('You have unsaved changes. Discard them and follow the link?')) {
                return;
            }
            setHasUnsavedChanges(false);
            // Applied HERE as well as through the route below, so a link to
            // the page the URL already names - after a wiki was chosen by hand
            // - still opens it: the route key would not change for that one.
            const match = PAGE_ROUTE.exec(path);
            if (match) chooseWiki(match[1], match[2]);
            navigate(path);
        });
        return () => setWikiNavigator(null);
    }, [navigate, chooseWiki]);

    // ⚠️ THE ROUTE IS FOLLOWED AFTER MOUNT TOO (review finding 6). The wiki
    // was seeded from the route at mount only, so an inline link into ANOTHER
    // wiki - navigate() on this already-mounted shell - changed routeWikiId and
    // nothing else: selectedWikiId kept the old wiki, initialPageId resolved to
    // null, and the click did nothing.
    //
    // Keyed on the ROUTE CHANGING, not on the route disagreeing with the
    // selection: a wiki chosen by hand while a deep-link URL is still showing
    // must win, and it would not if this compared against selectedWikiId.
    // (Clearing the URL on a hand selection instead remounts the shell under
    // the other <Route>, and the remount restores the persisted wiki - the
    // same defect by a longer road.)
    const routeKey = routeWikiId ? `${routeWikiId}/${routePageId}` : '';
    const appliedRouteRef = useRef(routeKey);
    useEffect(() => {
        if (routeKey === appliedRouteRef.current) return;
        appliedRouteRef.current = routeKey;
        if (routeWikiId) chooseWiki(routeWikiId, routePageId);
    }, [routeKey, routeWikiId, routePageId, chooseWiki]);

    /**
     * ⚠️ THE WIKI SELECTOR DEFAULTS TO THE OPEN WIKI, AND THAT IS THE WHOLE OF
     * R2. A single-wiki text search is a keyed Query; only "all wikis" reaches
     * the one scan this codebase grants for this feature, so the common case
     * must not be the one that scans.
     */
    const runSearch = async (e) => {
        e?.preventDefault?.();
        const q = pageQuery.trim();
        const tags = tagFilter.filter(Boolean);
        if (!q && tags.length === 0) return;

        setSearching(true);
        setSearchError(null);
        try {
            const params = new URLSearchParams();
            if (q) params.set('q', q);
            if (tags.length) params.set('tags', tags.join(','));
            if (!searchAllWikis && selectedWikiId) params.set('wikiId', selectedWikiId);

            const response = await apiFetch(`${API}/search?${params.toString()}`);
            const data = await response.json();
            if (!response.ok) {
                // The bounds live on the server (a one-character query is
                // refused there, not here), so its own message is the accurate
                // one to show.
                setSearchError(data.message || 'Search failed');
                setResults(null);
                return;
            }
            setResults(data);
        } catch (err) {
            console.error('TME wiki search failed:', err);
            setSearchError(err.message);
            setResults(null);
        } finally {
            setSearching(false);
        }
    };

    const openResult = (result) => {
        setResults(null);
        setPageQuery('');
        chooseWiki(result.wikiId, result.pageId);
    };

    const selectWiki = (wikiId) => {
        if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Discard them and switch?')) {
            return;
        }
        setHasUnsavedChanges(false);
        // A wiki chosen by hand supersedes a page a search result opened.
        chooseWiki(wikiId, null);
    };

    const createWiki = async () => {
        const name = newWikiName.trim();
        if (!name) return;
        setBusy(true);
        try {
            const response = await apiFetch(`${API}/wikis`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name })
            });
            const data = await response.json();
            if (!response.ok) {
                // A duplicate is a 409 NAMING the clash (D25). Show it: a
                // generic failure leaves the reader with nothing to change.
                window.alert(data.message || 'Could not create the wiki');
                return;
            }
            setNewWikiName('');
            setCreating(false);
            chooseWiki(data.wikiId, null);
            await fetchWikis();
        } finally {
            setBusy(false);
        }
    };

    const archiveWiki = async (wiki) => {
        if (!window.confirm(`Archive "${wiki.name}"? Its pages are archived with it, and the name is freed.`)) {
            return;
        }
        setBusy(true);
        try {
            const response = await apiFetch(`${API}/wikis/${wiki.wikiId}/archive`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version: wiki.version })
            });
            const data = await response.json();
            if (!response.ok) window.alert(data.message || 'Could not archive the wiki');
            await fetchWikis();
        } finally {
            setBusy(false);
        }
    };

    const restoreWiki = async (wiki) => {
        setBusy(true);
        try {
            const response = await apiFetch(`${API}/wikis/${wiki.wikiId}/restore`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version: wiki.version })
            });
            const data = await response.json();
            // A restore can 409 because the freed name was taken meanwhile - the
            // message says which wiki to rename first.
            if (!response.ok) window.alert(data.message || 'Could not restore the wiki');
            await fetchWikis();
        } finally {
            setBusy(false);
        }
    };

    const visibleWikis = wikis.filter(wiki =>
        !searchQuery.trim()
        || (wiki.name || '').toLowerCase().includes(searchQuery.trim().toLowerCase()));

    return (
        <div className="h-full flex overflow-hidden">
            <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
                <div className="px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                            <FaBook className="w-4 h-4" />
                            TME Wiki
                        </h2>
                        <button
                            type="button"
                            onClick={() => setCreating(true)}
                            className="text-primary-600 hover:text-primary-800"
                            title="New wiki"
                        >
                            <FaPlus className="w-3 h-3" />
                        </button>
                    </div>

                    <div className="relative mt-3">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                        <input
                            type="text"
                            aria-label="Filter wikis"
                            placeholder="Filter wikis..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-7 py-1.5 border border-gray-300 rounded text-sm"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear filter"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            >
                                <FaTimes className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                        <input
                            type="checkbox"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                        />
                        Show archived
                    </label>
                </div>

                {creating && (
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <input
                            type="text"
                            autoFocus
                            aria-label="New wiki name"
                            placeholder="Wiki name"
                            value={newWikiName}
                            maxLength={120}
                            onChange={(e) => setNewWikiName(e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <div className="flex gap-2 mt-2">
                            <button
                                type="button"
                                onClick={createWiki}
                                disabled={busy || !newWikiName.trim()}
                                className="px-3 py-1 bg-primary-600 text-white text-xs rounded disabled:opacity-50"
                            >
                                Create
                            </button>
                            <button
                                type="button"
                                onClick={() => { setCreating(false); setNewWikiName(''); }}
                                className="px-3 py-1 text-xs text-gray-600"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-2">
                    {loading && <p className="text-sm text-gray-500 px-2 py-3">Loading…</p>}
                    {error && !loading && <p className="text-sm text-red-600 px-2 py-3">{error}</p>}
                    {!loading && !error && visibleWikis.length === 0 && (
                        <p className="text-sm text-gray-500 italic px-2 py-3">No wikis yet</p>
                    )}
                    {visibleWikis.map(wiki => (
                        <div
                            key={wiki.wikiId}
                            role="button"
                            tabIndex={0}
                            className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 ${
                                wiki.wikiId === selectedWikiId ? 'bg-primary-50 border-l-2 border-primary-500' : ''
                            }`}
                            onClick={() => selectWiki(wiki.wikiId)}
                            onKeyDown={(e) => { if (e.key === 'Enter') selectWiki(wiki.wikiId); }}
                        >
                            <span className={`text-sm truncate ${wiki.archivedAt ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                                {wiki.name}
                            </span>
                            {wiki.archivedAt ? (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); restoreWiki(wiki); }}
                                    className="text-gray-400 hover:text-primary-600"
                                    title="Restore"
                                >
                                    <FaUndo className="w-3 h-3" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); archiveWiki(wiki); }}
                                    className="text-gray-400 hover:text-red-600"
                                    title="Archive"
                                >
                                    <FaArchive className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col">
                <form
                    onSubmit={runSearch}
                    className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2"
                >
                    <input
                        type="search"
                        aria-label="Search pages"
                        placeholder="Search page titles, tags and bodies…"
                        value={pageQuery}
                        onChange={(e) => setPageQuery(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600 whitespace-nowrap">
                        <input
                            type="checkbox"
                            checked={searchAllWikis}
                            onChange={(e) => setSearchAllWikis(e.target.checked)}
                        />
                        All wikis
                    </label>
                    <button
                        type="submit"
                        disabled={searching}
                        className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded disabled:opacity-50"
                    >
                        Search
                    </button>
                    {results && (
                        <button
                            type="button"
                            onClick={() => { setResults(null); setSearchError(null); }}
                            className="px-2 py-1.5 text-sm text-gray-600"
                        >
                            Close results
                        </button>
                    )}
                </form>

                {searchError && <p className="px-4 py-2 text-sm text-red-600">{searchError}</p>}

                {results ? (
                    <div className="flex-1 overflow-y-auto p-4">
                        {/* ⚠️ THE BOUND IS ALWAYS STATED (§8): how many pages
                            were looked at, and whether anything was dropped.
                            Without it "no results" and "results you cannot see"
                            look identical. */}
                        <p className="text-xs text-gray-500 mb-3">
                            {results.count} result{results.count === 1 ? '' : 's'} from{' '}
                            {results.pagesSearched} page{results.pagesSearched === 1 ? '' : 's'} searched
                            {results.truncated ? ' — more were found than are shown' : ''}
                            {results.scanned ? ' — searched every wiki' : ''}
                        </p>

                        {results.results.length === 0 && (
                            <p className="text-sm text-gray-500 italic">Nothing matched.</p>
                        )}

                        {results.results.map(result => (
                            <button
                                type="button"
                                key={`${result.wikiId}#${result.pageId}`}
                                onClick={() => openResult(result)}
                                className="block w-full text-left border border-gray-200 rounded p-3 mb-2 hover:bg-gray-50"
                            >
                                <span className="text-sm font-medium text-gray-800">{result.title}</span>
                                <span className="ml-2 text-xs text-gray-400">matched in {result.matchedIn}</span>
                                {/* Rendered as TEXT, never as HTML: a snippet is
                                    a slice of a page body, and a page body is
                                    the one thing that must not be re-parsed. */}
                                {result.snippet && (
                                    <span className="mt-1 block text-xs text-gray-600">{result.snippet}</span>
                                )}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="flex-1 overflow-hidden">
                        {selectedWikiId ? (
                            <TmeWikiEditorPane
                                wikiId={selectedWikiId}
                                wiki={wikis.find(w => w.wikiId === selectedWikiId) || null}
                                // ⚠️ THE ROUTE WINS OVER THE STORED VALUE (§14A).
                                // A deep link must open the page it names, not
                                // the one that happened to be open last time.
                                // The restored id is the LAST resort and is
                                // consumed once.
                                initialPageId={
                                    requestedPageId
                                    || (routeWikiId === selectedWikiId ? routePageId : null)
                                    || (selectedWikiId === initial.selectedWikiId
                                        ? restoredPageIdRef.current : null)
                                }
                                onPageSelect={setSelectedPageId}
                                tagFilter={tagFilter}
                                onTagFilterChange={setTagFilter}
                                scripts={scripts}
                                onRefreshScripts={() => loadScripts(true)}
                                onUnsavedChanges={setHasUnsavedChanges}
                                liveMessages={liveMessages}
                            />
                        ) : (
                            <div className="h-full flex items-center justify-center text-gray-500">
                                Select a wiki, or create one.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TmeWiki;
